import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, BookOpen, TrendingUp, Clock, AlertTriangle, Sun, Moon } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { setCredentials } from "../features/auth/authSlice";
import { useLoginMutation } from "../api/apiSlice";
import boyImage from "../img/HiPageLogo.png";

// Inline официальный Google G. Раньше тянули с svgrepo.com через <img>,
// на iOS Safari часть запросов отваливалась (ITP / network glitch).
// Inline SVG не зависит от сети и от CSP, всегда отрисуется.
const GoogleIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1 7.8 3l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.7 1 7.8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5A20 20 0 0 0 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.6-.4-3.5z"/>
  </svg>
);

// ─── HiPage ──────────────────────────────────────────────────────────
// Centered split-card: слева форма входа на сером, справа blue gradient hero
// с заголовком, списком фич (✓) и крупной иллюстрацией мальчика снизу.
//
// Сознательно простой layout — никаких feature-карточек / mockup'ов, юзер
// просил «как было раньше». Только добавлен boyImage в правую секцию + i18n.

export default function HiPage() {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  // Когда login возвращает 409 SESSION_LIMIT — храним {message, maxSessions} и
  // показываем модалку с кнопкой «завершить все другие сессии». Юзер либо
  // подтверждает (повторный login с forceLogout=true), либо отменяет.
  const [sessionLimit, setSessionLimit] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();

  // Google OAuth callback редиректит на /auth?error=session_limit при превышении
  // лимита. Тот же UX, что у email-логина — показываем модалку.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "session_limit") {
      setSessionLimit({
        maxSessions: Number(params.get("max")) || 2,
        message: t("hiPage.sessionLimitMessage", { count: Number(params.get("max")) || 2 }),
        fromOAuth: true,
      });
    }
  }, [location.search, t]);

  const handleGoogleLogin = () => {
    const googleAuthUrl = `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/auth/google`;
    window.location.href = googleAuthUrl;
  };

  const navigateByRole = (roles) => {
    if (roles.includes("admin")) navigate("/dashboard/admin");
    else if (roles.includes("instructor")) navigate("/dashboard/instructor");
    else navigate("/dashboard/student");
  };

  const doLogin = async ({ forceLogout = false } = {}) => {
    const response = await login({ email, password, forceLogout }).unwrap();
    dispatch(setCredentials(response));
    if (response.refreshToken) {
      localStorage.setItem("refreshToken", response.refreshToken);
    }
    navigateByRole(response.user.roles);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSessionLimit(null);
    try {
      await doLogin();
    } catch (err) {
      if (err?.status === 409 && err?.data?.code === "SESSION_LIMIT") {
        setSessionLimit({
          maxSessions: err.data.maxSessions || 2,
          message: err.data.message,
          fromOAuth: false,
        });
        return;
      }
      setError(err.data?.message || t("hiPage.loginError"));
    }
  };

  const handleForceLogout = async () => {
    setError(null);
    // OAuth-юзер: повторно идём через Google, но с ?force=1 — backend сначала
    // сбросит все его сессии, потом залогинит. Email/пароль здесь не вводится.
    if (sessionLimit?.fromOAuth) {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      window.location.href = `${apiUrl}/auth/google?force=1`;
      return;
    }
    // Email/password: повторно дёргаем login с forceLogout=true.
    try {
      await doLogin({ forceLogout: true });
      setSessionLimit(null);
    } catch (err) {
      setError(err.data?.message || t("hiPage.loginError"));
      setSessionLimit(null);
    }
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem("i18nextLng", lng); } catch { /* ignore */ }
  };

  return (
    /* Мобильный скролл-фикс: вместо flex items-center (который ломал скролл,
       когда контент выше viewport) используем block-layout с padding.
       min-h-screen — Tailwind fallback (100vh), inline style 100dvh — для
       современных браузеров (без address-bar issue на iOS). На sm+ возвращаем
       flex-центрирование, потому что там карточка точно влезает. */
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-gray-100 to-blue-100 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950 px-3 py-4 sm:p-4 sm:flex sm:items-center sm:justify-center"
      style={{ minHeight: '100dvh' }}
    >
      {/* SESSION_LIMIT модалка — отдельный layer, не ломает основной layout */}
      {sessionLimit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setSessionLimit(null)}
        >
          <div
            className="bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-700 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("hiPage.sessionLimitTitle")}
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                  {sessionLimit.message || t("hiPage.sessionLimitMessage", { count: sessionLimit.maxSessions })}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                type="button"
                onClick={() => setSessionLimit(null)}
                className="w-full py-2.5 px-4 bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition text-sm"
              >
                {t("hiPage.sessionLimitCancel")}
              </button>
              {/* Force-logout работает в обоих режимах:
                  - email/password: повторно POST /auth/login с forceLogout=true
                  - OAuth: redirect на /auth/google?force=1 (backend сбросит
                    все сессии в callback и залогинит заново). */}
              <button
                type="button"
                onClick={handleForceLogout}
                disabled={isLoading}
                className="w-full py-2.5 px-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition text-sm disabled:opacity-60"
              >
                {isLoading ? t("hiPage.signingIn") : t("hiPage.sessionLimitForce")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-6xl mx-auto bg-white dark:bg-slate-900 shadow-2xl rounded-3xl overflow-hidden flex flex-col lg:flex-row lg:items-stretch ring-1 ring-slate-200/60 dark:ring-slate-800">
        {/* ─── LEFT: форма входа ───────────────────────────────────── */}
        <div className="relative w-full lg:w-1/2 px-7 py-7 sm:px-10 sm:py-8 flex flex-col justify-start bg-gray-50 dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800">
          {/* Lang switcher + Theme toggle в углу — нужны юзеру ДО логина:
              язык чтобы вообще понимать форму, тема — визуальный комфорт. */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white shadow-sm transition"
              aria-label="Тема"
              title={theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}
            >
              {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-0.5 text-[11px] shadow-sm">
              <button
                type="button"
                onClick={() => changeLang("ru")}
                className={`px-2 py-0.5 rounded-md font-semibold transition ${
                  i18n.resolvedLanguage === "ru"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                RU
              </button>
              <button
                type="button"
                onClick={() => changeLang("kk")}
                className={`px-2 py-0.5 rounded-md font-semibold transition ${
                  i18n.resolvedLanguage === "kk"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                KK
              </button>
            </div>
          </div>

          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 text-center leading-tight">
            <Trans
              i18nKey="hiPage.welcomeTitle"
              components={{ 1: <span className="text-blue-600 dark:text-blue-400" /> }}
            />
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 mb-5 text-center max-w-sm mx-auto">
            {t("hiPage.welcomeSubtitle")}
          </p>

          {error && (
            <div className="bg-red-100 dark:bg-red-950/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg mb-3 text-center text-xs">
              {error}
            </div>
          )}

          <form onSubmit={handleLoginSubmit} className="space-y-4 max-w-md mx-auto w-full">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1.5">
                {t("hiPage.email")}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("hiPage.emailPlaceholder")}
                className="block w-full px-4 py-3 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-base"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1.5">
                {t("hiPage.password")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("hiPage.passwordPlaceholder")}
                  className="block w-full px-4 py-3 pr-11 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember + Forgot */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-gray-600 dark:text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-800 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                <span>{t("hiPage.rememberMe")}</span>
              </label>
              <button
                type="button"
                className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition"
                onClick={() => navigate("/forgot-password")}
              >
                {t("hiPage.forgotPassword")}
              </button>
            </div>

            {/* Войти + Регистрация — side by side */}
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                type="submit"
                className="w-full py-3 flex justify-center items-center bg-blue-600 text-white text-base font-semibold rounded-xl shadow-md hover:bg-blue-700 transition disabled:opacity-60"
                disabled={isLoading}
              >
                {isLoading ? t("hiPage.signingIn") : t("hiPage.signIn")}
              </button>
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="w-full py-3 flex justify-center items-center bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-100 text-base font-semibold rounded-xl shadow-md hover:bg-gray-300 dark:hover:bg-slate-600 transition"
              >
                {t("hiPage.register")}
              </button>
            </div>

            {/* Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-200 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition shadow-sm flex items-center justify-center gap-2.5 font-medium text-base disabled:opacity-60"
            >
              <GoogleIcon className="w-5 h-5" />
              {t("hiPage.signInWithGoogle")}
            </button>
          </form>

          {/* Teacher CTA — компактный inline */}
          <div className="mt-5 pt-4 border-t border-gray-200 dark:border-slate-700 max-w-md mx-auto w-full flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {t("hiPage.teacherCardTitle")}
              </div>
              <div className="text-xs text-gray-600 dark:text-slate-400 leading-tight">
                {t("hiPage.teacherCardDesc")}
              </div>
            </div>
            <a
              href="/#become-instructor"
              className="shrink-0 inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 transition"
            >
              {t("hiPage.teacherCardCta")}
            </a>
          </div>
        </div>

        {/* ─── RIGHT: hero с мальчиком ─────────────────────────────── */}
        <div className="relative w-full lg:w-1/2 px-7 py-7 sm:px-10 sm:py-8 flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 text-white overflow-hidden min-h-[520px] lg:min-h-[600px]">
          {/* Brand top-right */}
          <div className="absolute top-5 right-5 text-base font-semibold opacity-80 z-20">
            Uaqyt
          </div>

          {/* Контент сверху: заголовок + иконки. Ограничен слева, чтобы бала справа не перекрывал текст. */}
          <div className="relative z-10 max-w-full lg:max-w-[58%]">
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold leading-tight mb-3">
              <Trans
                i18nKey="hiPage.hero.headline"
                components={{ 1: <span className="text-blue-200" />, br: <br /> }}
              />
            </h1>
            <p className="text-sm sm:text-base opacity-90 max-w-md mb-5">
              {t("hiPage.hero.tagline")}
            </p>
            <ul className="space-y-2.5 text-sm sm:text-base opacity-95">
              <li className="flex items-start gap-3">
                <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
                  <BookOpen size={16} className="text-blue-100" />
                </span>
                <span className="pt-1">{t("hiPage.hero.bullet1")}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
                  <TrendingUp size={16} className="text-blue-100" />
                </span>
                <span className="pt-1">{t("hiPage.hero.bullet2")}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
                  <Clock size={16} className="text-blue-100" />
                </span>
                <span className="pt-1">{t("hiPage.hero.bullet3")}</span>
              </li> 
            </ul>
          </div>

          {/* Мальчик — absolute, спущен ниже дна колонки (ноги/кружка выглядывают за край). */}
          <img
            src={boyImage}
            alt=""
            className="absolute -bottom-6 sm:-bottom-8 lg:-bottom-20 right-0 lg:-right-2 h-[80%] sm:h-[90%] lg:h-[100%] w-auto z-0 pointer-events-none object-contain object-bottom"
            style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.35))" }}
          />
        </div>
      </div>
    </div>
  );
}
